import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  dashboardService,
  DashboardOverview,
  DashboardSignal,
  DashboardTabKey,
} from '../services/dashboardService';
import { API_BASE_URL, certificatesApi, getAuthToken, jobsApi, settingsApi } from '../utils/api';

// Query keys for consistent cache management
export const queryKeys = {
  dashboard: ['dashboard'] as const,
  dashboardOverview: ['dashboard', 'overview'] as const,
  dashboardTab: (tabKey: DashboardTabKey) => ['dashboard', 'tab', tabKey] as const,
  jobs: ['jobs'] as const,
  jobsAll: ['jobs', 'all'] as const,
  clients: ['clients'] as const,
  certificates: ['certificates'] as const,
  certificateTemplates: ['certificates', 'templates'] as const,
  invoices: ['invoices'] as const,
  quotes: ['quotes'] as const,
  engineers: ['engineers'] as const,
  settings: ['settings'] as const,
} as const;

export type SettingsPayload = {
  user?: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    address?: string;
    gasSafeNumber?: string;
    dateOfBirth?: string;
  };
  company?: {
    name?: string;
    registrationNumber?: string;
    vatNumber?: string;
    gasRegistrationNumber?: string;
    address?: string;
    phone?: string;
    businessEmail?: string;
    workingHoursStart?: string;
    workingHoursEnd?: string;
    vatRate?: number;
  };
  settings?: {
    bankSortCode?: string;
    bankAccountNumber?: string;
    paymentTermsDays?: number;
    invoiceFooter?: string;
    invoicePrefix?: string;
    invoiceNextNumber?: number;
    certificatePrefix?: string;
    certificateNextNumber?: number;
    notificationEmail?: boolean;
    notificationSms?: boolean;
    autoReminders?: boolean;
  };
};

export const useSettingsData = () => {
  return useQuery({
    queryKey: queryKeys.settings,
    queryFn: async () => {
      const response = await settingsApi.get();
      return response.data;
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

export const useCertificateTemplatesData = () => {
  return useQuery({
    queryKey: queryKeys.certificateTemplates,
    queryFn: async () => {
      const response = await certificatesApi.listTemplates();
      const payload = response.data;
      if (payload?.success && payload?.data) return payload.data;
      return payload?.data || payload || [];
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

export const useChangePassword = () => {
  return useMutation({
    mutationFn: async (payload: { currentPassword: string; newPassword: string }) => {
      const response = await settingsApi.changePassword(payload);
      return response.data;
    },
  });
};

export const useUpdateSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: SettingsPayload) => {
      const response = await settingsApi.update(payload);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.settings, data);
    },
  });
};

export const useTeamData = () => {
  return useQuery({
    queryKey: ['settings', 'team'],
    queryFn: async () => {
      const response = await settingsApi.listTeam();
      return response.data;
    },
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

export const useInviteMember = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { email: string; role?: string }) => settingsApi.inviteMember(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['settings', 'team'] }),
  });
};

export const useUpdateMemberRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      settingsApi.updateMemberRole(userId, role),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['settings', 'team'] }),
  });
};

export const useRemoveMember = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => settingsApi.removeMember(userId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['settings', 'team'] }),
  });
};

export const useCancelInvite = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (inviteId: string) => settingsApi.cancelInvite(inviteId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['settings', 'team'] }),
  });
};

export const useResendInvite = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (inviteId: string) => settingsApi.resendInvite(inviteId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['settings', 'team'] }),
  });
};

export const useMfaStatus = () => {
  return useQuery({
    queryKey: ['settings', 'mfa'],
    queryFn: async () => {
      const response = await settingsApi.getMfaStatus();
      return response.data;
    },
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

export const useDisableMfa = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (factorId: string) => settingsApi.disableMfa(factorId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['settings', 'mfa'] }),
  });
};

// Dashboard data hook with caching
export const useDashboardData = () => {
  return useQuery({
    queryKey: queryKeys.dashboard,
    queryFn: () => dashboardService.getDashboardData(),
    staleTime: 2 * 60 * 1000, // 2 minutes - dashboard data can be slightly stale
    gcTime: 5 * 60 * 1000, // 5 minutes in cache
    refetchOnWindowFocus: false,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

export const useDashboardOverview = () => {
  return useQuery<DashboardOverview>({
    queryKey: queryKeys.dashboardOverview,
    queryFn: () => dashboardService.getOverview(),
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

export const useDashboardTab = (tabKey: DashboardTabKey) => {
  return useQuery<DashboardSignal[]>({
    queryKey: queryKeys.dashboardTab(tabKey),
    queryFn: () => dashboardService.getTab(tabKey),
    staleTime: 30 * 1000,
    gcTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

// Jobs data hook with caching
export const useJobsData = () => {
  return useQuery({
    queryKey: queryKeys.jobsAll,
    queryFn: async () => {
      console.log('🔄 Fetching jobs from cache or API...');

      const response = await fetch(`${API_BASE_URL}/jobs?limit=1000`, {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || `API responded with ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.data) {
        console.log('✅ Jobs fetched from API:', data.data.length, 'jobs');
        return {
          jobs: data.data,
          engineers: data.data
            .filter((job: any) => job.assignedEngineer && job.assignedEngineer.name)
            .reduce((acc: any[], job: any) => {
              const engineer = job.assignedEngineer;
              if (!acc.find((e) => e.id === engineer.id)) {
                acc.push(engineer);
              }
              return acc;
            }, []),
        };
      }

      return {
        jobs: data.data || data || [],
        engineers: [],
      };
    },
    staleTime: 1 * 60 * 1000, // 1 minute - jobs data should be fresher
    gcTime: 3 * 60 * 1000, // 3 minutes in cache
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

// Clients data hook
export const useClientsData = () => {
  return useQuery({
    queryKey: queryKeys.clients,
    queryFn: async () => {
      console.log('🔄 Fetching clients from cache or API...');

      const response = await fetch(`${API_BASE_URL}/clients?limit=1000`, {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || `API responded with ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.data) {
        console.log('✅ Clients fetched from API:', data.data.length, 'clients');
        return data.data;
      }

      return data.data || data || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - client data changes less frequently
    gcTime: 10 * 60 * 1000, // 10 minutes in cache
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

// Certificates data hook
export const useCertificatesData = () => {
  return useQuery({
    queryKey: queryKeys.certificates,
    queryFn: async () => {
      console.log('🔄 Fetching certificates from cache or API...');

      const response = await fetch(`${API_BASE_URL}/certificates?limit=1000`, {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || `API responded with ${response.status}`);
      }

      const data = await response.json();
      if (data.success && data.data) {
        console.log('✅ Certificates fetched from API:', data.data.length, 'certificates');
        return data.data;
      }

      return data.data || data || [];
    },
    staleTime: 3 * 60 * 1000, // 3 minutes
    gcTime: 6 * 60 * 1000, // 6 minutes in cache
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

// Invoices data hook
export const useInvoicesData = () => {
  return useQuery({
    queryKey: queryKeys.invoices,
    queryFn: async () => {
      console.log('🔄 Fetching invoices from cache or API...');

      const response = await fetch(`${API_BASE_URL}/invoices?limit=1000`, {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || `API responded with ${response.status}`);
      }

      const data = await response.json();
      if (data.success && data.data) {
        console.log('✅ Invoices fetched from API:', data.data.length, 'invoices');
        return data.data;
      }

      return data.data || data || [];
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes in cache
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

// Re-export quotes hooks from dedicated file
export { useQuotesData, useQuoteData, useQuoteStats, useCreateQuote, useUpdateQuote, useUpdateQuoteStatus, useDeleteQuote, useConvertQuoteToJob, useSendQuote, useRefreshQuotes } from './useQuotesData';

// Mutation hooks for data updates
export const useRefreshDashboard = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: () => dashboardService.getDashboardData(),
    onSuccess: (data) => {
      // Update the cache with fresh data
      queryClient.setQueryData(queryKeys.dashboard, data);
    },
  });
};

export const useUpdateJobStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, note }: { id: string; status: string; note?: string }) =>
      jobsApi.updateStatus(id, status, note).then((r: any) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.jobsAll });
    },
  });
};

export const useRefreshJobs = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async () => {
      // Fetch fresh jobs data
      const response = await fetch(`${API_BASE_URL}/jobs?limit=1000`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) throw new Error(`API responded with ${response.status}`);
      
      const data = await response.json();
      return data.success ? data.data : [];
    },
    onSuccess: (jobs) => {
      // Update the cache with fresh data
      const engineers = jobs
        .filter((job: any) => job.assignedEngineer && job.assignedEngineer.name)
        .reduce((acc: any[], job: any) => {
          const engineer = job.assignedEngineer;
          if (!acc.find(e => e.id === engineer.id)) {
            acc.push(engineer);
          }
          return acc;
        }, []);
      
      queryClient.setQueryData(queryKeys.jobsAll, { jobs, engineers });
    },
  });
};

// Utility hook to invalidate and refetch all data
export const useRefreshAllData = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async () => {
      // Invalidate all queries to trigger fresh fetches
      await queryClient.invalidateQueries();
      return true;
    },
  });
};

// Hook to prefetch data (useful for preloading)
export const usePrefetchData = () => {
  const queryClient = useQueryClient();
  
  const prefetchDashboard = () => {
    queryClient.prefetchQuery({
      queryKey: queryKeys.dashboard,
      queryFn: () => dashboardService.getDashboardData(),
      staleTime: 2 * 60 * 1000,
    });
  };
  
  const prefetchJobs = () => {
    queryClient.prefetchQuery({
      queryKey: queryKeys.jobsAll,
      queryFn: async () => {
        const response = await fetch(`${API_BASE_URL}/jobs?limit=1000`, {
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) throw new Error(`API responded with ${response.status}`);
        
        const data = await response.json();
        return data.success ? { jobs: data.data, engineers: [] } : { jobs: [], engineers: [] };
      },
      staleTime: 1 * 60 * 1000,
    });
  };
  
  return { prefetchDashboard, prefetchJobs };
};
